import React from "react";
import "../../../AllComponentCSS.css";
import CodeCopyBlock from "../../../CodeCopyBlock";

export default function RWD_Frameworks() {
  return (
    <div className="tutorial-container">
      {/* Hero Section */}
      <div
        style={{
          background: "linear-gradient(135deg, #34d399, #059669)",
          color: "white",
          padding: "2rem",
          borderRadius: "16px",
          marginBottom: "2rem",
          textAlign: "center",
        }}
      >
        <h1 style={{ fontSize: "2.5rem", fontWeight: "bold" }}>
          🛠️ RWD Frameworks
        </h1>
        <p style={{ fontSize: "1.2rem", marginTop: "1rem" }}>
          Responsive web design frameworks provide prebuilt components, grid
          systems, and utilities to quickly create layouts that adapt to any
          screen size.
        </p>
        <img
          src="https://getbootstrap.com/docs/5.3/assets/brand/bootstrap-logo-shadow.png"
          alt="RWD Frameworks"
          style={{
            maxWidth: "150px",
            margin: "1.5rem auto 0",
            borderRadius: "12px",
            boxShadow: "0 6px 20px rgba(0,0,0,0.2)",
          }}
        />
      </div>

      {/* Intro */}
      <p className="tutorial-intro">
        Popular RWD frameworks like <strong>Bootstrap</strong>,{" "}
        <strong>Tailwind CSS</strong>, and <strong>Foundation</strong> offer
        grid systems, responsive utilities, and pre-designed components. These
        frameworks make it easier to implement responsive layouts without
        writing all CSS from scratch.
      </p>

      {/* Key Points */}
      <div
        style={{
          background: "linear-gradient(135deg, #bbf7d0, #86efac)",
          padding: "1.2rem",
          paddingLeft: "35px",
          borderRadius: "12px",
          marginBottom: "2rem",
        }}
      >
        <h2 style={{ color: "#166534" }}>🔑 Key Points</h2>
        <ul className="tutorial-list-points">
          <li>
            Frameworks provide prebuilt **grid systems** for responsive layouts.
          </li>
          <li>
            Use classes for spacing, typography, and responsive breakpoints.
          </li>
          <li>
            Pre-designed components like buttons, navbars, cards, and modals
            save development time.
          </li>
          <li>
            Most frameworks are mobile-first, adapting naturally to smaller
            screens.
          </li>
          <li>
            Customize using themes, configuration files, or utility classes.
          </li>
        </ul>
      </div>

      {/* Example 1: Bootstrap Grid */}
      <h2 className="section-title">1️⃣ Bootstrap Responsive Grid</h2>
      <CodeCopyBlock
        code={`<div class="container">
  <div class="row">
    <div class="col-sm-6 col-md-4">Column 1</div>
    <div class="col-sm-6 col-md-4">Column 2</div>
    <div class="col-sm-6 col-md-4">Column 3</div>
  </div>
</div>`}
      />
      <p>
        ✅ Columns automatically stack on smaller screens and spread on larger
        screens.
      </p>

      {/* Example 2: Tailwind CSS Responsive Utilities */}
      <h2 className="section-title">2️⃣ Tailwind CSS Responsive Example</h2>
      <CodeCopyBlock
        code={`<div class="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
  <div class="bg-blue-500 p-4 text-white">Box 1</div>
  <div class="bg-green-500 p-4 text-white">Box 2</div>
  <div class="bg-red-500 p-4 text-white">Box 3</div>
</div>`}
      />
      <p>
        ✅ Grid columns change automatically at small (sm) and large (lg)
        breakpoints.
      </p>

      {/* Example 3: Foundation Grid */}
      <h2 className="section-title">3️⃣ Foundation Responsive Grid</h2>
      <CodeCopyBlock
        code={`<div class="grid-x grid-padding-x">
  <div class="cell small-6 medium-4">Cell 1</div>
  <div class="cell small-6 medium-4">Cell 2</div>
  <div class="cell small-6 medium-4">Cell 3</div>
</div>`}
      />
      <p>
        ✅ Foundation uses a flexible grid system similar to Bootstrap with
        small and medium breakpoints.
      </p>

      {/* Next Steps */}
      <div
        style={{
          background: "linear-gradient(135deg, #fef3c7, #fde68a)",
          padding: "1.2rem",
          paddingLeft: "35px",
          borderRadius: "12px",
          marginBottom: "2rem",
        }}
      >
        <h2 style={{ color: "#92400e" }}>🚀 Next Steps</h2>
        <ul className="tutorial-list-points">
          <li>
            Practice using Bootstrap or Tailwind grid classes to create
            responsive layouts.
          </li>
          <li>
            Combine prebuilt components with your own CSS for customization.
          </li>
          <li>
            Learn breakpoints and responsive utilities for spacing, typography,
            and display.
          </li>
          <li>
            Experiment with combining multiple frameworks in a project (careful
            with conflicts).
          </li>
          <li>
            Test layouts on different devices or browser widths to verify
            responsiveness.
          </li>
        </ul>
      </div>
    </div>
  );
}
